import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { ApiError, api } from '../../lib/api';
import { pollUntil, uploadBanner } from '../../lib/upload';
import { useSession } from '../../stores/session';
import { banners, gradientCss, type BannerGradient, type BannerPreset } from '../profile/banner';

type Phase = 'idle' | 'uploading' | 'processing' | 'done';

/**
 * The profile header: pick a gradient, or upload something.
 *
 * ## The gradient is the default, not the fallback
 *
 * `profiles.banner_key` existed from the first migration with nothing able to
 * set it, so every profile on the site rendered the same grey hatch. Fixing
 * that with an upload form alone would have fixed it for the handful of people
 * who upload one; deriving a gradient from the username fixes it for everybody,
 * immediately, without anyone opening this page.
 *
 * So the picker opens on eight gradients with one already applied, and the
 * upload is the second thing rather than the first.
 *
 * ## Nothing is selected until something is chosen
 *
 * A derived gradient shows as "yours by default" rather than as selected. The
 * difference is real: picking the one you were given is a decision, and it
 * stops the header changing if the derivation ever does.
 *
 * ## Which is also why removing an image is safe
 *
 * There is no empty state to fall into. `DELETE /me/banner` leaves the gradient
 * underneath, so the button is a complete action rather than one that leaves a
 * hole somebody then has to fill.
 */
export function BannerPicker({
  gradient,
  imageUrl,
}: {
  gradient: BannerGradient | undefined;
  imageUrl: string | null | undefined;
}) {
  const input = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const user = useSession((s) => s.user);

  const [phase, setPhase] = useState<Phase>('idle');
  const [percent, setPercent] = useState(0);
  const [failure, setFailure] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ['banner-presets'],
    queryFn: () => banners.presets(),
    // A fixed list that describes nothing about anybody. Refetching it is pure
    // cost.
    staleTime: Infinity,
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['profile'] });
  };

  const choose = useMutation({
    mutationFn: (preset: string | null) => banners.choose(preset),
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: () => banners.remove(),
    onSuccess: async () => {
      setPhase('idle');
      await refresh();
    },
  });

  async function upload(file: File) {
    setFailure(null);
    setPercent(0);
    setPhase('uploading');

    try {
      await uploadBanner(file, { onProgress: setPercent });
      setPhase('processing');

      /*
       * The API said yes; the worker has not finished. Poll the profile until
       * the image appears, compared against the URL we started with so this
       * cannot be satisfied by the *previous* banner still being there.
       */
      const ready = await pollUntil(
        () => api.get<{ banner: string | null }>(`/users/${user?.username}`),
        (profile) => profile.banner !== null && profile.banner !== imageUrl,
      );

      if (!ready) {
        /*
         * One case reaches here having actually worked: re-uploading bytes
         * identical to the current header. `ProcessBanner` keys on the content,
         * so the same image produces the same URL and there is no change to
         * wait for. Nothing visible was going to happen either way.
         */
        const now = await api.get<{ banner: string | null }>(`/users/${user?.username}`);

        if (now.banner === null || now.banner !== imageUrl) {
          setFailure('Still processing. It should appear shortly — try reloading in a moment.');
          setPhase('idle');

          return;
        }
      }

      setPhase('done');
      await refresh();
    } catch (error) {
      /*
       * Two quite different failures land here. A 422 is the API refusing the
       * file and its message names the actual problem; anything else is the PUT
       * to storage failing, which for a cross-origin request is very often CORS
       * and the browser deliberately tells script nothing useful about it.
       */
      setFailure(
        error instanceof ApiError
          ? error.fieldErrors.content_type ?? error.fieldErrors.size_bytes ?? error.message
          : (error as Error).message,
      );
      setPhase('idle');
    }
  }

  const presets = data?.presets ?? [];
  const busy = phase === 'uploading' || phase === 'processing';

  return (
    <div className="flex flex-col gap-4" data-banner-picker>
      {/* ------------------------------------------------------------ */}
      {/* What the header looks like right now                          */}
      {/* ------------------------------------------------------------ */}
      <div
        data-banner-preview
        className="relative h-[110px] w-full overflow-hidden rounded-[var(--radius-card)] border border-line"
        style={{ background: gradient ? gradientCss(gradient) : undefined }}
      >
        {imageUrl && <img src={imageUrl} alt="" className="h-full w-full object-cover" />}
      </div>

      {/* ------------------------------------------------------------ */}
      {/* Gradients                                                     */}
      {/* ------------------------------------------------------------ */}
      <div className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between gap-3">
          <span className="meta-caps text-faint">gradient</span>
          {gradient?.derived && (
            <span className="font-mono text-[10px] text-ghost">
              yours by default — pick one to change it
            </span>
          )}
        </div>

        <div className="grid grid-cols-4 gap-2">
          {presets.map((preset) => (
            <Swatch
              key={preset.preset}
              preset={preset}
              // A derived gradient is not a choice, so nothing reads as chosen.
              selected={!gradient?.derived && gradient?.preset === preset.preset}
              disabled={choose.isPending}
              onSelect={() => choose.mutate(preset.preset)}
            />
          ))}
        </div>

        {imageUrl && (
          <p className="font-mono text-[10px] leading-relaxed text-ghost">
            your uploaded header covers this — remove it to see the gradient
          </p>
        )}
      </div>

      {/* ------------------------------------------------------------ */}
      {/* Or an image                                                   */}
      {/* ------------------------------------------------------------ */}
      <div className="flex flex-col gap-2 border-t border-line pt-4">
        <span className="meta-caps text-faint">or upload a header</span>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" disabled={busy} onClick={() => input.current?.click()}>
            {imageUrl ? 'Replace header' : 'Upload a header'}
          </Button>

          {imageUrl && (
            <Button
              variant="ghost"
              data-banner-remove
              disabled={busy || remove.isPending}
              onClick={() => remove.mutate()}
            >
              Remove
            </Button>
          )}
        </div>

        <span className="text-xs text-faint">
          JPEG, PNG, WebP or GIF. Up to 8MB. Wide images work best — 2400×560 is the size it is
          shown at.
        </span>

        <input
          ref={input}
          type="file"
          // The native control is hidden behind a styled button, but it is still
          // a real input - so keyboard and screen-reader users get the system
          // file picker rather than a div pretending to be one.
          className="sr-only"
          accept="image/jpeg,image/png,image/webp,image/gif"
          onChange={(event) => {
            const file = event.target.files?.[0];

            // Reset, so choosing the same file twice after a failure still
            // fires a change event.
            event.target.value = '';

            if (file) void upload(file);
          }}
        />
      </div>

      {phase === 'uploading' && (
        <div className="flex flex-col gap-1">
          <div className="h-1.5 overflow-hidden rounded-full bg-raised">
            <div
              className="h-full rounded-full bg-accent transition-[width] duration-150"
              style={{ width: `${percent}%` }}
            />
          </div>
          <span className="text-xs text-faint">Uploading… {percent}%</span>
        </div>
      )}

      {/* Named explicitly. "Upload complete but the image is not there yet" is
          the confusing moment. */}
      {phase === 'processing' && <span className="text-xs text-faint">Uploaded. Resizing…</span>}

      {phase === 'done' && <Alert tone="success">Header updated.</Alert>}
      {failure && <Alert tone="error">{failure}</Alert>}
      {choose.isError && <Alert tone="error">That gradient could not be applied.</Alert>}
    </div>
  );
}

function Swatch({
  preset,
  selected,
  disabled,
  onSelect,
}: {
  preset: BannerPreset;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      title={preset.label}
      data-banner-preset={preset.preset}
      disabled={disabled}
      onClick={onSelect}
      className={`flex flex-col gap-1.5 rounded-[var(--radius-control)] border p-1.5 text-left transition-colors ${
        selected ? 'border-fresh' : 'border-line hover:border-line-strong'
      }`}
    >
      <span
        aria-hidden="true"
        className="block h-9 w-full rounded-[calc(var(--radius-control)-4px)]"
        style={{ background: gradientCss(preset) }}
      />
      <span className={`font-mono text-[10px] ${selected ? 'text-fresh' : 'text-faint'}`}>
        {preset.label}
      </span>
    </button>
  );
}
